import {
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { DeleteTrashIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useUser } from "@js/apps/common/hooks/use-user";
import { RouterLink } from "@js/components/link";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch, useNavigate } from "@js/hooks";
import { formatDate } from "@js/utils/date";
import { DateFormats } from "@js/utils/date/types";
import { pluralize } from "@js/utils/string";

import { rostersApi, useDeleteRosterMutation } from "../../api";
import type { RosterSummary } from "../../types";
import { openDeleteRosterConfirmModal } from "../delete-roster-confirm-modal";
import { openRosterFormModal } from "../roster-form-modal";
import { RosterVisibilityChip } from "../roster-visibility-chip";

export type RosterListItemProps = {
  roster: RosterSummary;
  animationDelayMs?: number;
};

export const RosterListItem = ({
  roster,
  animationDelayMs = 0,
}: RosterListItemProps) => {
  const user = useUser();
  const navigate = useNavigate();
  const [deleteRoster] = useDeleteRosterMutation();
  const dispatch = useAppDispatch();
  const isOwner = roster.owner === user?.id;
  const canChangeVisibility =
    isOwner || roster.visibility !== ENUMS.RosterVisibility.PUBLIC;

  const handleAddDescriptionClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    openRosterFormModal({ roster, canChangeVisibility });
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();

    openDeleteRosterConfirmModal({
      title: roster.title,
      onConfirm: async () => {
        try {
          await deleteRoster(roster.id).unwrap();
          dispatch(
            rostersApi.util.invalidateTags([
              { type: "Rosters", id: roster.id },
            ]),
          );
          Snackbar.success("Roster deleted.");
        } catch {
          Snackbar.error("Something went wrong.");
        }
      },
    });
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      onClick={() => navigate(`/rosters/${roster.id}/`)}
      sx={{
        justifyContent: "space-between",
        alignItems: { sm: "center" },
        gap: { xs: 1, sm: 2 },
        py: 2.5,
        px: 2.5,
        cursor: "pointer",
        bgcolor: "var(--white)",
        borderRadius: "0.75rem",
        boxShadow: "var(--elevation-2)",
        opacity: 0,
        animation: "roster-row-in 300ms ease-out forwards",
        animationDelay: `${animationDelayMs}ms`,
        transition: "box-shadow 150ms ease, transform 150ms ease",
        "@media (hover: hover) and (pointer: fine)": {
          "&:hover": {
            boxShadow: "var(--elevation-4)",
            transform: "translateY(-1px)",
          },
          "&:hover .roster-delete-btn, &:focus-within .roster-delete-btn": {
            opacity: 1,
          },
        },
        "@keyframes roster-row-in": {
          from: {
            opacity: 0,
            transform: "translateY(4px)",
          },
          to: {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
      }}
    >
      <Stack sx={{ gap: 0.5, minWidth: 0 }}>
        <RouterLink
          to={`/rosters/${roster.id}/`}
          onClick={(event) => event.stopPropagation()}
          style={{ display: "block", minWidth: 0 }}
        >
          <Typography
            component="span"
            variant="paragraph"
            fontWeight={500}
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {roster.title}
          </Typography>
        </RouterLink>
        {roster.description ? (
          <Typography
            component="p"
            variant="paragraph"
            size="small"
            sx={{
              color: "var(--grey-2)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {roster.description}
          </Typography>
        ) : (
          <Typography
            component="p"
            variant="paragraph"
            size="small"
            sx={{ color: "var(--grey-2)", fontStyle: "italic" }}
          >
            This roster doesn&apos;t have a description yet.{" "}
            {isOwner && (
              <Typography
                component="span"
                variant="paragraph"
                size="small"
                onClick={handleAddDescriptionClick}
                role="button"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleAddDescriptionClick(event);
                  }
                }}
                sx={{
                  color: "var(--grey-2)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Add one
              </Typography>
            )}
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        sx={{ gap: { xs: 1.5, sm: 3 }, alignItems: "center", flexShrink: 0 }}
      >
        <RosterVisibilityChip visibility={roster.visibility} />
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          sx={{ color: "var(--grey-2)", whiteSpace: "nowrap" }}
        >
          {roster.freelancer_ids.length} freelancer
          {pluralize(roster.freelancer_ids.length)}
        </Typography>
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          sx={{ color: "var(--grey-2)", whiteSpace: "nowrap" }}
        >
          {formatDate(roster.created, DateFormats["Jan 1, 1970"])}
        </Typography>
        <Stack sx={{ width: "2rem", flexShrink: 0, alignItems: "flex-end" }}>
          {isOwner && (
            <Tooltip title="Delete roster">
              <IconButton
                aria-label="Delete roster"
                size="small"
                variant="transparent"
                onClick={handleDelete}
                className="roster-delete-btn"
                sx={{
                  transition: "opacity 150ms ease",
                  "@media (hover: hover) and (pointer: fine)": {
                    opacity: 0,
                  },
                }}
              >
                <DeleteTrashIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
